/**
 * RiskManagerService.js
 * Service for real risk analysis from user's actual trading positions.
 * v4.9.10: Replaced mock data with live calculations from /api/runs/positions/all.
 */
import StrategyEngineService from './StrategyEngineService';

const fetchRiskData = async (portfolioId = null) => {
  // Fetch real positions from the backend (user-scoped via JWT)
  let positions = [];
  try {
    positions = await StrategyEngineService.getAllPositions() || [];
  } catch (e) {
    console.error('[RiskManager] Failed to fetch positions:', e);
  }

  const open = positions.filter(p => p.status === 'open' || p.status === 'pending');
  const closed = positions.filter(p => p.status === 'closed');
  const all = positions;

  const riskData = {
    portfolioSummary: buildPortfolioSummary(open, closed, all),
    positionRisks: buildPositionRisks(open),
    riskMetrics: buildRiskMetrics(open, closed),
    riskSettings: getRiskSettings(),
    riskAlerts: buildRiskAlerts(open, closed),
    updatedAt: new Date().toISOString()
  };
  
  return riskData;
};

// ============================================================================
// REAL DATA BUILDERS — Calculate from user's actual positions
// ============================================================================

function buildPortfolioSummary(open, closed, all) {
  const totalMargin = open.reduce((sum, p) => sum + ((p.quantity || 0) * (p.entryPrice || 0)), 0);
  const unrealizedPnl = open.reduce((sum, p) => sum + (parseFloat(p.pnl) || 0), 0);
  const totalValue = totalMargin + unrealizedPnl;

  // Daily P&L: closed trades from today + unrealized
  const today = new Date().toISOString().split('T')[0];
  const todayClosed = closed.filter(p => (p.closedAt || p.exitTime || '').startsWith(today));
  const dailyPnl = todayClosed.reduce((s, p) => s + (parseFloat(p.pnl) || 0), 0) + unrealizedPnl;

  // Weekly/Monthly P&L
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString();
  const weeklyPnl = closed.filter(p => (p.closedAt || '') >= weekAgo).reduce((s, p) => s + (parseFloat(p.pnl) || 0), 0) + unrealizedPnl;
  const monthlyPnl = closed.filter(p => (p.closedAt || '') >= monthAgo).reduce((s, p) => s + (parseFloat(p.pnl) || 0), 0) + unrealizedPnl;

  // Exposure by side
  const longExposure = open.filter(p => p.side === 'buy').reduce((s, p) => s + ((p.quantity || 0) * (p.currentPrice || p.entryPrice || 0) * (p.leverage || 1)), 0);
  const shortExposure = open.filter(p => p.side === 'sell').reduce((s, p) => s + ((p.quantity || 0) * (p.currentPrice || p.entryPrice || 0) * (p.leverage || 1)), 0);
  const grossExposure = longExposure + shortExposure;
  const netExposure = totalValue > 0 ? ((longExposure - shortExposure) / totalValue * 100) : 0;

  // Allocation by trading mode
  const byMode = {};
  open.forEach(p => {
    const mode = p.tradingMode || 'spot';
    byMode[mode] = (byMode[mode] || 0) + ((p.quantity || 0) * (p.entryPrice || 0));
  });
  const modeTotal = Object.values(byMode).reduce((s, v) => s + v, 0) || 1;

  return {
    totalValue: totalValue.toFixed(2),
    pnl: {
      daily: dailyPnl.toFixed(2),
      weekly: weeklyPnl.toFixed(2),
      monthly: monthlyPnl.toFixed(2),
      yearly: monthlyPnl.toFixed(2), // approximate
    },
    allocations: {
      spot: ((byMode.spot || 0) / modeTotal * 100).toFixed(2),
      margin: ((byMode.margin || 0) / modeTotal * 100).toFixed(2),
      futures: ((byMode.futures || 0) / modeTotal * 100).toFixed(2),
      cash: open.length === 0 ? '100.00' : '0.00',
    },
    exposures: {
      net: netExposure.toFixed(2),
      gross: grossExposure.toFixed(2),
      long: longExposure.toFixed(2),
      short: shortExposure.toFixed(2),
    }
  };
}

function buildPositionRisks(open) {
  return open.map(pos => {
    const entryPrice = pos.blendedEntryPrice || pos.averageEntryPrice || pos.entryPrice || 0;
    const currentPrice = pos.currentPrice || entryPrice;
    const quantity = pos.quantity || 0;
    const leverage = pos.leverage || 1;
    const side = pos.side || 'buy';
    const margin = quantity * entryPrice;
    const notional = margin * leverage;
    const unrealizedPnl = parseFloat(pos.pnl) || 0;
    const pnlPct = parseFloat(pos.pnlPercent) || 0;

    return {
      symbol: pos.symbol,
      type: 'crypto',
      sector: 'Crypto',
      position: side === 'buy' ? 'long' : 'short',
      size: notional.toFixed(2),
      allocation: '0', // calculated per-card
      unrealizedPnl: unrealizedPnl.toFixed(2),
      valueAtRisk: (notional * 0.03).toFixed(2), // 3% VaR estimate
      riskContribution: '0',
      stopLoss: {
        price: (pos.dynamicStopLoss || pos.trailingSLLevel || 0).toFixed(4),
        distance: (pos.dynamicStopLoss || 1.5).toFixed(2),
        type: pos.trailingStopActivated ? 'trailing' : 'fixed'
      },
      maxDrawdown: Math.abs(parseFloat(pos.maxAdverseExcursion) || pnlPct).toFixed(2),
      leverage: leverage.toFixed(0),
      tradingMode: pos.tradingMode || 'spot',
      volatility: '0',
      correlations: { marketIndex: '0', sectorIndex: '0' }
    };
  });
}

function buildRiskMetrics(open, closed) {
  const allPnls = closed.map(p => parseFloat(p.pnl) || 0);
  const wins = allPnls.filter(p => p > 0);
  const losses = allPnls.filter(p => p < 0);
  const totalPnl = allPnls.reduce((s, p) => s + p, 0);
  const avgReturn = allPnls.length > 0 ? totalPnl / allPnls.length : 0;
  const variance = allPnls.length > 1 ? allPnls.reduce((s, p) => s + Math.pow(p - avgReturn, 2), 0) / allPnls.length : 0;
  const stdDev = Math.sqrt(variance);
  const sharpe = stdDev > 0 ? (avgReturn / stdDev) : 0;
  const downside = losses.length > 0 ? Math.sqrt(losses.reduce((s, p) => s + p * p, 0) / losses.length) : 0;
  const sortino = downside > 0 ? (avgReturn / downside) : 0;

  // Max drawdown from cumulative P&L
  let peak = 0, maxDD = 0, cumulative = 0;
  allPnls.forEach(p => { cumulative += p; if (cumulative > peak) peak = cumulative; const dd = peak - cumulative; if (dd > maxDD) maxDD = dd; });
  const maxDDPct = peak > 0 ? (maxDD / peak * 100) : 0;

  // Current exposure
  const totalNotional = open.reduce((s, p) => s + ((p.quantity || 0) * (p.currentPrice || p.entryPrice || 0) * (p.leverage || 1)), 0);
  const dailyVaR = totalNotional * 0.02; // 2% daily VaR estimate

  // Exposure by strategy
  const byStrategy = {};
  open.forEach(p => {
    const name = p.strategyName || p.strategyId?.slice(0, 8) || 'Unknown';
    byStrategy[name] = (byStrategy[name] || 0) + ((p.quantity || 0) * (p.entryPrice || 0));
  });
  const stratTotal = Object.values(byStrategy).reduce((s, v) => s + v, 0) || 1;

  // Exposure by leverage tier
  const byLevTier = {};
  open.forEach(p => {
    const lev = p.leverage || 1;
    const tier = lev >= 20 ? '20x+' : lev >= 10 ? '10x' : lev >= 5 ? '5x' : 'Spot/1x';
    byLevTier[tier] = (byLevTier[tier] || 0) + ((p.quantity || 0) * (p.entryPrice || 0));
  });
  const levTotal = Object.values(byLevTier).reduce((s, v) => s + v, 0) || 1;

  return {
    valueAtRisk: {
      daily: { amount: dailyVaR.toFixed(2), percent: (totalNotional > 0 ? dailyVaR / totalNotional * 100 : 0).toFixed(2) },
      weekly: { amount: (dailyVaR * 2.24).toFixed(2), percent: (totalNotional > 0 ? dailyVaR * 2.24 / totalNotional * 100 : 0).toFixed(2) },
      monthly: { amount: (dailyVaR * 4.47).toFixed(2), percent: (totalNotional > 0 ? dailyVaR * 4.47 / totalNotional * 100 : 0).toFixed(2) },
    },
    expectedShortfall: {
      amount: (dailyVaR * 1.5).toFixed(2),
      percent: (totalNotional > 0 ? dailyVaR * 1.5 / totalNotional * 100 : 0).toFixed(2),
    },
    sharpeRatio: sharpe.toFixed(2),
    sortinoRatio: sortino.toFixed(2),
    beta: '1.00',
    alpha: avgReturn.toFixed(4),
    maxDrawdown: {
      current: maxDDPct.toFixed(2),
      historical: maxDDPct.toFixed(2),
    },
    stressTestResults: {
      marketCrash: (-(totalNotional * 0.15)).toFixed(2),
      interestRateHike: (-(totalNotional * 0.05)).toFixed(2),
      commoditySurge: (-(totalNotional * 0.08)).toFixed(2),
      volatilitySpike: (-(totalNotional * 0.10)).toFixed(2),
    },
    riskExposures: {
      sectors: Object.entries(byStrategy).map(([name, val]) => ({
        name,
        allocation: (val / stratTotal * 100).toFixed(2),
        risk: (val / stratTotal * 100 * 0.3).toFixed(2),
      })),
      geographies: Object.entries(byLevTier).map(([name, val]) => ({
        name,
        allocation: (val / levTotal * 100).toFixed(2),
        risk: (val / levTotal * 100 * 0.4).toFixed(2),
      })),
    },
  };
}

function getRiskSettings() {
  return {
    general: {
      maxPositionSize: "5%",
      maxSectorExposure: "30%",
      maxDrawdownTolerance: "15%",
      leverageLimit: "20x",
      volatilityTarget: "12%",
      rebalancingFrequency: "Per-scan"
    },
    stopLoss: {
      defaultType: "Trailing",
      defaultDistance: "1.5%",
      trailingStopActivation: "0.5%",
      stopLossRequired: true
    },
    alerts: {
      portfolioValueDrop: "5%",
      positionValueDrop: "3%",
      volatilityIncrease: "30%",
      correlationThreshold: "0.8"
    },
    riskLimits: {
      valueAtRiskLimit: "3%",
      expectedShortfallLimit: "5%",
      leverageWarning: "10x",
      concentrationWarning: "25%"
    }
  };
}

function buildRiskAlerts(open, closed) {
  const alerts = [];
  let id = 1;

  // Check for high-loss open positions
  open.forEach(p => {
    const pnlPct = parseFloat(p.pnlPercent) || 0;
    if (pnlPct < -2) {
      alerts.push({
        id: id++, type: 'position_loss', severity: pnlPct < -5 ? 'high' : 'medium',
        title: `${p.symbol} at ${pnlPct.toFixed(2)}% loss`,
        description: `Open position ${p.symbol} (${p.side}) is at ${pnlPct.toFixed(2)}% P&L with ${p.leverage || 1}x leverage`,
        timestamp: new Date().toISOString(), status: 'active'
      });
    }
  });

  // Check for high leverage concentration
  const highLev = open.filter(p => (p.leverage || 1) >= 10);
  if (highLev.length >= 3) {
    alerts.push({
      id: id++, type: 'leverage', severity: 'medium',
      title: `${highLev.length} positions at 10x+ leverage`,
      description: `You have ${highLev.length} open positions with 10x or higher leverage, increasing risk of large losses`,
      timestamp: new Date().toISOString(), status: 'active'
    });
  }

  // Check recent loss streak
  const recentClosed = closed.slice(-10);
  const recentLosses = recentClosed.filter(p => (parseFloat(p.pnl) || 0) < 0);
  if (recentLosses.length >= 7) {
    alerts.push({
      id: id++, type: 'loss_streak', severity: 'high',
      title: `${recentLosses.length}/10 recent trades are losses`,
      description: `Your last 10 closed trades have ${recentLosses.length} losses. Consider reducing position sizes or pausing trading.`,
      timestamp: new Date().toISOString(), status: 'active'
    });
  }

  return alerts;
}

// This service would handle real communication with the nodes in a production app
const RiskManagerService = {
  getRiskAnalysis: async (portfolioId = null) => {
    try {
      return await fetchRiskData(portfolioId);
    } catch (error) {
      console.error('Error fetching risk data:', error);
      throw error;
    }
  },
  
  updateRiskSettings: async (settings) => {
    try {
      // In a real app, this would send the settings to the backend
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, message: 'Risk settings updated successfully' };
    } catch (error) {
      console.error('Error updating risk settings:', error);
      throw error;
    }
  },
  
  resolveAlert: async (alertId) => {
    try {
      // In a real app, this would send the alert resolution to the backend
      await new Promise(resolve => setTimeout(resolve, 300));
      return { success: true, message: 'Alert resolved successfully' };
    } catch (error) {
      console.error('Error resolving alert:', error);
      throw error;
    }
  },
  
  runStressTest: async (portfolioId, scenario) => {
    try {
      // In a real app, this would initiate a stress test via the risk manager node
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Mock response with stress test results
      const hash = portfolioId ? 
        portfolioId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 
        Math.floor(Math.random() * 1000);
      
      const randomFactor = (hash % 20) / 10;
      
      return {
        success: true,
        scenario,
        impactPercentage: (-10 - randomFactor * 8).toFixed(2),
        impactAmount: (-5000 - randomFactor * 4000).toFixed(2),
        worstPositions: [
          { symbol: 'AAPL', impact: (-12 - randomFactor * 8).toFixed(2) },
          { symbol: 'MSFT', impact: (-9 - randomFactor * 7).toFixed(2) },
          { symbol: 'BTC', impact: (-18 - randomFactor * 10).toFixed(2) }
        ],
        recommendations: [
          "Reduce technology sector exposure",
          "Consider hedging market risk with VIX derivatives",
          "Add uncorrelated assets to reduce drawdown risk"
        ]
      };
    } catch (error) {
      console.error('Error running stress test:', error);
      throw error;
    }
  }
};

export default RiskManagerService;
